/**
 * Pure functions of the `word_ladder` type: the starter definition, the progress blob and the
 * submission. No component here, so `module.ts` stays light in the eager bundle.
 */
import { normalizeWord } from "./ladder";
import {
  MAX_RUNGS,
  type WordLadderDefinition,
  type WordLadderProgress,
  type WordLadderSubmission,
} from "./types";

/** Same starting point as the server's `empty_definition()`. */
export function emptyDefinition(): WordLadderDefinition {
  return {
    puzzleType: "word_ladder",
    typeVersion: 1,
    language: "en",
    start: "COLD",
    end: "WARM",
    maxSteps: 5,
    rulesText: {
      en: "Climb from the first word to the last, changing exactly one letter at each step without moving the others. Every word in between must be in the dictionary. When a step limit is shown, the ladder may not take more steps than that.",
      fr: "Passez du premier mot au dernier en changeant exactement une lettre à chaque étape, sans déplacer les autres. Chaque mot intermédiaire doit être dans le dictionnaire. Si un nombre d'étapes maximal est indiqué, l'échelle ne doit pas le dépasser.",
    },
  };
}

export function emptyProgress(_definition: WordLadderDefinition): WordLadderProgress {
  return { version: 1, rungs: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: WordLadderDefinition,
  raw: unknown,
): WordLadderProgress {
  if (!isRecord(raw) || !Array.isArray(raw.rungs)) return emptyProgress(definition);
  const rungs = raw.rungs
    .filter((word): word is string => typeof word === "string")
    .map(normalizeWord)
    .filter((word) => /^[A-Z]{1,32}$/.test(word))
    .slice(0, MAX_RUNGS);
  return { version: 1, rungs };
}

/** The whole ladder: the start word, the player's rungs, the end word. */
export function serializeSubmission(
  definition: WordLadderDefinition,
  progress: WordLadderProgress,
): WordLadderSubmission {
  return { ladder: [definition.start, ...progress.rungs, definition.end] };
}
